import { Button } from "../ui/Button"
import { useTranslation } from "react-i18next"
import { getProductName } from "@/utils/product-name"
import { Dialog } from "@/components/ui/Dialog"

import { useUserQuery } from "@/api/users-api"
import {
	useCreateCheckoutSessionMutation,
	useOpenCustomerPortalMutation,
	usePaymentPlansQuery,
} from "@/api/payments-api"
import { Icon } from "../icons/Icon"
import clsx from "clsx"
import { ReactNode } from "react"
import { PaymentPlan } from "@/api/dto/payment"
import { GetUserResponseBody } from "@/api/dto/users"
import { activePolarrSubscription } from "@/api/user-helpers"
import { uuid } from "@/utils/uuid"
import { analytics } from "@/telemetry/amplitude"

export type SubscriptionTerm = "yearly" | "monthly"

const formatCost = (
	cost: { value: number; currency: string },
	options?: {
		maximumSignificantDigits?: number
		maximumFractionDigits?: number
	},
) => {
	const moneyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: cost.currency.toUpperCase(),
		maximumSignificantDigits: options?.maximumSignificantDigits,
		maximumFractionDigits: options?.maximumFractionDigits,
	})

	return moneyFormatter.format(cost.value * 1e-2)
}

const calculateCost = (plan: PaymentPlan) => {
	const cost = plan.cost
	const isMonthly = plan.term === "monthly"
	const exports = plan.metadata.exports

	const monthlyValue = isMonthly
		? cost.value
		: // Always make the monthly cost of yearly plans appear to end in .99 even if it
			// comes out to slightly less (e.g. yearly is $599, monthly is $49.92, but show $49.99)
			Math.floor(cost.value / 1200) * 100 + 99
	const yearlyValue = isMonthly ? cost.value * 12 : cost.value
	const singleValue = isMonthly ? monthlyValue / exports : yearlyValue / exports

	return {
		monthly: formatCost(
			{ value: monthlyValue, currency: cost.currency },
			{ maximumFractionDigits: 2 },
		),
		yearly: formatCost(
			{ value: yearlyValue, currency: cost.currency },
			{ maximumFractionDigits: 0 },
		),
		single: formatCost(
			{ value: singleValue, currency: cost.currency },
			{ maximumSignificantDigits: 2 },
		),
	}
}

const Subscription: React.FC<{
	plan: PaymentPlan
	selectedTerm: SubscriptionTerm
	user?: GetUserResponseBody
	origin?: string
}> = ({ plan, selectedTerm, user, origin }) => {
	const { t } = useTranslation("components")
	const returnTo = window.location.pathname + window.location.search + window.location.hash
	const openCheckoutSession = useCreateCheckoutSessionMutation(returnTo)
	const openCustomerPortal = useOpenCustomerPortalMutation(returnTo)
	const activeSubscription = activePolarrSubscription(user)
	const expiringSubscription =
		user?.subscriptions?.filter(
			(subscription) => subscription.status === "subscription_canceled",
		)?.[0] ?? null

	const cost = calculateCost(plan)
	const isActive =
		activeSubscription?.tier === plan.tier && activeSubscription?.term === plan.term
	const isExpiring =
		expiringSubscription?.tier === plan.tier && expiringSubscription?.term === plan.term
	const isUnlimited = plan.metadata.hasUnlimitedExports === true
	const planName =
		(plan.metadata["displayName"] as string) ??
		`${(plan.tier ?? "pro").toString()}_${plan.term ?? "monthly"}`
	const planPrice =
		typeof plan.cost?.value === "number" ? Number((plan.cost.value / 100).toFixed(2)) : null

	let buttonText = t("subscriptionsDialog.subscribe")
	let buttonTrackingLabel = "subscribe"
	if (isActive) {
		buttonText = t("subscriptionsDialog.cancelSubscription")
		buttonTrackingLabel = "cancel_subscription"
	} else if (isExpiring) {
		buttonText = t("subscriptionsDialog.renewSubscription")
		buttonTrackingLabel = "renew"
	} else if (activeSubscription || expiringSubscription) {
		buttonText = t("subscriptionsDialog.switchPlan")
		buttonTrackingLabel = "switch_plan"
	}

	const isHighlighted = plan.tier === "unlimited"

	return (
		<div
			key={plan.tier}
			className={clsx(
				(isActive || isExpiring) && "ring-2 ring-white",
				isHighlighted ? "bg-accent-700" : "bg-base-700",
				"flex flex-col space-y-4 overflow-hidden rounded-lg",
			)}
		>
			<div
				className={clsx(
					isHighlighted ? "bg-accent-900" : "bg-base-900",
					"flex w-full flex-row items-center justify-center space-x-2.5 py-2 text-center font-bold",
				)}
			>
				<div className="text-sm">
					{((plan.metadata["displayName"] as string) ?? plan.tier ?? "").toUpperCase()}
				</div>
			</div>

			<div className="flex h-full flex-col space-y-4 px-3 pb-6">
				<div className="my-2 flex flex-col items-center space-y-4">
					<div className="text-xxl font-semibold">
						{cost.monthly}
						<span className="text-lg">{t("legacySubscriptionsDialog.mo")}</span>
					</div>
					{isActive && (
						<div
							className={clsx(
								isHighlighted ? "bg-accent-900" : "bg-base-900",
								"mt-4 rounded-full px-2 py-1 text-center text-xs text-base-200",
							)}
						>
							{t("subscriptionsDialog.currentPlan")}
						</div>
					)}
					{!isActive && isExpiring && expiringSubscription && (
						<div
							className={clsx(
								isHighlighted ? "bg-accent-900" : "bg-base-900",
								"rounded-full bg-accent-900 px-2 py-1 text-center text-xs text-base-50",
							)}
						>
							{t("subscriptionsDialog.expiringAt", {
								date: new Date(expiringSubscription.expiresAt).toLocaleDateString(),
							})}
						</div>
					)}
					{!isActive && !isExpiring && (
						<div
							className={clsx(
								isHighlighted ? "text-base-200" : "text-base-400",
								"mt-4 min-h-8 whitespace-pre-line text-center text-xs text-base-200",
							)}
						>
							{selectedTerm === "monthly"
								? t("subscriptionsDialog.billedMonthlyShort")
								: t("subscriptionsDialog.billedYearly", { cost: cost.yearly })}
						</div>
					)}
				</div>

				<div className="flex grow flex-col">
					<div className="my-2 border-b border-base-100/10"></div>
					<Checkpoint
						content={
							<>
								<strong>{t("subscriptionsDialog.unlimited")}</strong>{" "}
								{t("subscriptionsDialog.unlimitedAiEdits")}
							</>
						}
						color={isHighlighted ? "text-base-200" : "text-base-400"}
					/>
					<div className="my-2 border-b border-base-100/10"></div>
					{isUnlimited && (
						<Checkpoint
							content={
								<>
									<strong>{t("subscriptionsDialog.unlimited")}</strong>{" "}
									{t("subscriptionsDialog.unlimitedExports")}
								</>
							}
							color={isHighlighted ? "text-base-200" : "text-base-400"}
						/>
					)}
					{!isUnlimited && (
						<Checkpoint
							content={
								<>
									<strong>{plan.metadata.exports}</strong>{" "}
									{t("legacySubscriptionsDialog.exportsPer")}{" "}
									{plan.term == "monthly" ? "month" : "year"}
								</>
							}
							color={isHighlighted ? "text-base-200" : "text-base-400"}
						/>
					)}
					<div className="my-2 border-b border-base-100/10"></div>
				</div>

				<Button
					trackingLabel={buttonTrackingLabel}
					className={clsx(isHighlighted && "bg-accent-600")}
					onClick={() => {
						if (!isActive) {
							analytics.trackEvent({
								label: "subscription_start",
								type: "click",
								customProperties: {
									origin: origin ?? null,
									planName,
									planInterval: plan.term ?? null,
									planPrice,
								},
							})
						}

						if (isActive) {
							openCustomerPortal.mutate()
						} else {
							openCheckoutSession.mutate({
								priceId: plan.paymentProviders.stripe.priceId,
							})
						}
					}}
				>
					{buttonText}
				</Button>
			</div>
		</div>
	)
}

const Checkpoint: React.FC<{ content: ReactNode; color: string }> = ({ content, color }) => (
	<div className={clsx("flex flex-row items-center space-x-1", color)}>
		<Icon className="shrink-0" size={14} name="check" />
		<div className="text-sm">{content}</div>
	</div>
)

const Plans: React.FC<{
	selectedTerm: SubscriptionTerm
	origin?: string
}> = ({ selectedTerm, origin }) => {
	const userQuery = useUserQuery()
	const user = userQuery.data
	const paymentPlansQuery = usePaymentPlansQuery()
	const plans: PaymentPlan[] = paymentPlansQuery.data?.data ?? []

	const selectedPlan = plans.filter((plan) => plan.term === selectedTerm)[0]

	return (
		<>
			{selectedPlan && (
				<div className={clsx("grid gap-4", `grid-cols-1`)}>
					<Subscription
						plan={selectedPlan}
						user={user}
						key={uuid()}
						selectedTerm={selectedTerm}
						origin={origin}
					/>
				</div>
			)}
		</>
	)
}

export const LegacySubscriptionsDialog = (props: {
	initialTerm?: SubscriptionTerm
	origin?: string
}) => {
	const { t } = useTranslation("components")
	const currentTerm = props.initialTerm ?? "yearly"

	return (
		<Dialog trackingViewName="subscription_dialog">
			<div className="flex w-[360px] flex-col space-y-4">
				<div className="flex flex-row items-center justify-center space-x-4 pb-1">
					<div
						className={
							"rounded-full bg-base-900/50 px-4 py-2 text-sm font-bold text-base-300"
						}
					>
						{currentTerm.toUpperCase()}
					</div>
				</div>
				<Plans selectedTerm={currentTerm} origin={props.origin} />
				<div className="justify-center text-center text-xs text-base-600">
					{t("subscriptionsDialog.exportExplanation", { productName: getProductName() })}
				</div>
			</div>
		</Dialog>
	)
}
